import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import type {
  OTCFieldsData,
  OTCFixedRateLoanFieldsData,
  OTCFloatingRateLoanFieldsData,
  OTCForwardFieldsData,
  OTCOptionFieldsData,
  OTCRepoFieldsData,
} from '@src/types/OTC';
import {
  isFixedRateLoanProductFields,
  isFixedRateLoanProductRow,
  isFloatingRateLoanProductFields,
  isFloatingRateLoanProductRow,
  isForwardProductFields,
  isForwardProductRow,
  isOptionProductFields,
  isOptionProductRow,
  isRepoProductFields,
  isRepoProductRow,
} from '@src/types/OTC/otcGuards';
import type {
  DealProductBuilderRow,
  ForwardDealProductBuilderRow,
  OptionDealProductBuilderRow,
  RepoDealProductBuilderRow,
} from '@src/types/OTC/otcProductBuilderTypes';
import type { DealBookingType } from '@src/types/OTC/otcSharedTypes';
import { formatLoanSecurityTypeDisplay, isUnsecuredLoanSecurityType } from '@src/utils/OTC/loanSecurity';
import { useFixingIndexMapping } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import {
  Button,
  ButtonVariants,
  CardRow,
  CurrencyRenderer,
  Dialog,
  DialogContent,
  HStack,
  logger,
  OptionTypeEnum,
  Stack,
  VStack,
  type Leaves,
  type useDisclosureWithContext,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { applyForwardOtcFieldsUpdate } from '../productDefinitions/applyForwardOtcFieldsUpdate';
import { applyOptionOtcFieldsUpdate } from '../productDefinitions/applyOptionOtcFieldsUpdate';
import { getSliceStrategyForRow } from '../productDefinitions/getSliceStrategy';
import { FixedRateLoanProductLayout } from './FixedRateLoanProductLayout';
import { FloatingRateLoanProductLayout } from './FloatingRateLoanProductLayout';
import { ForwardProductLayout } from './ForwardProductLayout';
import { OptionProductLayout } from './OptionProductLayout';
import { RepoProductLayout } from './RepoProductLayout';
import { withDefaultLoanPaymentCcyFromBorrowedAsset } from './loanPaymentCurrencyForRow';
import type { OTCFieldsChangeHandler, OTCFieldsContext } from './types';
import { useOTCFieldsValidation } from './useOTCFieldsValidation';

type OTCProductType = 'forward' | 'fixedRateLoan' | 'floatingRateLoan' | 'option' | 'repo';

interface OTCProductDisplayMap {
  productType: string;
  rateType?: string;
}

const OTC_PRODUCT_DISPLAY_MAP: Record<DealBookingType, OTCProductDisplayMap> = {
  forward: {
    productType: 'Forward',
  },
  fixedRateLoan: {
    productType: 'Loan',
    rateType: 'Fixed Rate',
  },
  floatingRateLoan: {
    productType: 'Loan',
    rateType: 'Floating Rate',
  },
  option: {
    productType: 'Vanilla Option',
  },
  repo: {
    productType: 'Repo',
  },
};

type DialogData = OTCFieldsData & {
  baseAsset: DealProductBuilderRow['baseAsset'];
};

const collateralFromRow = (row: DealProductBuilderRow): string | null | undefined =>
  'collateralAsset' in row ? row.collateralAsset : undefined;

/** OTC dialog fields only (excludes grid-only `baseAsset` merged into dialog state for loan defaults). */
const dialogDataToOtcFields = (data: DialogData): OTCFieldsData => {
  const { baseAsset: _baseAsset, ...otcFields } = data;
  return otcFields as OTCFieldsData;
};

const cloneOptionRowForLocalUpdate = (row: OptionDealProductBuilderRow): OptionDealProductBuilderRow => ({
  ...row,
  otcFields: row.otcFields == null ? null : { ...row.otcFields },
});

const cloneForwardRowForLocalUpdate = (row: ForwardDealProductBuilderRow): ForwardDealProductBuilderRow => ({
  ...row,
  otcFields: row.otcFields == null ? null : { ...row.otcFields },
});

/**
 * Local OTC dialog state. Forward and Option rows run their OTC field update helpers {@link applyForwardOtcFieldsUpdate} and {@link applyOptionOtcFieldsUpdate} on each change so
 * inverse payoff and index rules match Redux; other products use a shallow field merge until Confirm
 * dispatches `updateOTCFields`.
 */
const useOTCFieldsForm = (disclosure: OTCFieldsDialogProps['disclosure']) => {
  const fixingIndexMapping = useFixingIndexMapping();

  const [formData, setFormData] = useState<DialogData>((): DialogData => {
    if (!disclosure.context) {
      throw new Error('Context always required for OTCFieldsDialog');
    }

    const { sliceStrategy } = getSliceStrategyForRow(disclosure.context.rowData);
    const rowData = disclosure.context.rowData;
    const otcWithPaymentCcyDefault = withDefaultLoanPaymentCcyFromBorrowedAsset(
      sliceStrategy.getDefaultValues(rowData.otcFields),
      rowData.baseAsset,
      collateralFromRow(rowData)
    );
    return {
      ...otcWithPaymentCcyDefault,
      baseAsset: rowData.baseAsset,
    };
  });

  // Use the new Yup-based validation
  const { errors, touched, requiredFields, setFieldTouched, isValid, setAllFieldsTouched } = useOTCFieldsValidation({
    formData,
  });

  const handleFieldChange: OTCFieldsChangeHandler = useCallback(
    (fieldName, value) => {
      if (!disclosure.context) {
        return;
      }

      const rowData = disclosure.context.rowData;

      setFormData(prevData => {
        if (isOptionProductRow(rowData)) {
          const proposedOtcFields = {
            ...dialogDataToOtcFields(prevData),
            [fieldName]: value,
          } as OTCOptionFieldsData;

          const syntheticRow: OptionDealProductBuilderRow = {
            ...rowData,
            otcFields: dialogDataToOtcFields(prevData) as OptionDealProductBuilderRow['otcFields'],
          };
          const localRow = cloneOptionRowForLocalUpdate(syntheticRow);
          applyOptionOtcFieldsUpdate(localRow, proposedOtcFields, fixingIndexMapping);

          const nextOtcFields = localRow.otcFields;
          if (nextOtcFields == null) {
            return prevData;
          }

          return {
            ...prevData,
            ...nextOtcFields,
            baseAsset: prevData.baseAsset,
          } as DialogData;
        }

        if (isForwardProductRow(rowData)) {
          const proposedOtcFields = {
            ...dialogDataToOtcFields(prevData),
            [fieldName]: value,
          } as OTCForwardFieldsData;

          const syntheticRow: ForwardDealProductBuilderRow = {
            ...rowData,
            otcFields: dialogDataToOtcFields(prevData) as ForwardDealProductBuilderRow['otcFields'],
          };
          const localRow = cloneForwardRowForLocalUpdate(syntheticRow);
          applyForwardOtcFieldsUpdate(localRow, proposedOtcFields, fixingIndexMapping);

          const nextOtcFields = localRow.otcFields;
          if (nextOtcFields == null) {
            return prevData;
          }

          return {
            ...prevData,
            ...nextOtcFields,
            baseAsset: prevData.baseAsset,
          } as DialogData;
        }

        return {
          ...prevData,
          [fieldName]: value,
        } as DialogData;
      });

      setTimeout(() => {
        setFieldTouched(fieldName);
      }, 0);
    },
    [disclosure.context, fixingIndexMapping, setFieldTouched]
  );

  const handleClear = useCallback(() => {
    if (disclosure.context) {
      const rowData = disclosure.context.rowData;
      const { baseAsset } = rowData;
      const { sliceStrategy } = getSliceStrategyForRow(rowData);
      const clearedData = sliceStrategy.getDefaultValues(null);
      if (!clearedData) {
        return;
      }
      const otcWithPaymentCcyDefault = withDefaultLoanPaymentCcyFromBorrowedAsset(
        clearedData,
        baseAsset,
        collateralFromRow(rowData)
      );
      setFormData({
        ...otcWithPaymentCcyDefault,
        baseAsset,
      });
    }
  }, [disclosure.context]);

  return {
    formData,
    isValid,
    errors,
    touched,
    requiredFields,
    handleFieldChange,
    handleClear,
    setAllFieldsTouched,
  };
};

const OTCFieldsDialogContent = ({
  disclosure,
  disclosureContext,
  onSave,
}: OTCFieldsDialogProps & { disclosureContext: OTCFieldsContext }) => {
  const { enableLoanOpenTermSupport, enableOTCPhysicalSettlement } = useFeatureFlag();
  const { formData, isValid, errors, touched, requiredFields, handleFieldChange, setAllFieldsTouched, handleClear } =
    useOTCFieldsForm(disclosure);

  const handleSave = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const otcFieldsOnly = dialogDataToOtcFields(formData);
    let dataToSave: OTCFieldsData = otcFieldsOnly;
    if (isRepoProductFields(otcFieldsOnly) && otcFieldsOnly.termType === 'OpenTerm') {
      // Open repos have no fixed term; exclude term fields from the saved payload
      dataToSave = { ...otcFieldsOnly, term: null, termCustomUnits: undefined, termCustomValue: undefined };
    }
    onSave(dataToSave);
    disclosure.close();
  };

  const handleCancel = () => {
    disclosure.close();
  };

  const renderProductLayout = () => {
    if (isForwardProductFields(formData)) {
      const typedHandler: (field: Leaves<OTCForwardFieldsData>, value: string | boolean | null | undefined) => void = (
        field,
        value
      ) => {
        handleFieldChange(field as Leaves<OTCFieldsData>, value);
      };
      return (
        <ForwardProductLayout
          formData={formData}
          disclosureContext={disclosureContext}
          handleFieldChange={typedHandler}
          errors={errors}
          touched={touched}
          requiredFields={requiredFields}
          enableOTCPhysicalSettlement={enableOTCPhysicalSettlement}
        />
      );
    }

    if (isFixedRateLoanProductFields(formData)) {
      const typedHandler: (
        field: Leaves<OTCFixedRateLoanFieldsData>,
        value: string | boolean | null | undefined
      ) => void = (field, value) => {
        handleFieldChange(field as Leaves<OTCFieldsData>, value);
      };
      return (
        <FixedRateLoanProductLayout
          formData={formData}
          disclosureContext={disclosureContext}
          handleFieldChange={typedHandler}
          errors={errors}
          touched={touched}
          requiredFields={requiredFields}
          enableLoanOpenTermSupport={enableLoanOpenTermSupport}
        />
      );
    }

    if (isOptionProductFields(formData)) {
      const typedHandler: OTCFieldsChangeHandler<OTCOptionFieldsData> = (fieldName, value) => {
        handleFieldChange(fieldName, value);
      };
      return (
        <OptionProductLayout
          formData={formData}
          disclosureContext={disclosureContext}
          handleFieldChange={typedHandler}
          errors={errors}
          touched={touched}
          requiredFields={requiredFields}
          enableOTCPhysicalSettlement={enableOTCPhysicalSettlement}
        />
      );
    }

    if (isFloatingRateLoanProductFields(formData)) {
      const typedHandler: (
        field: Leaves<OTCFloatingRateLoanFieldsData>,
        value: string | boolean | null | undefined
      ) => void = (field, value) => {
        handleFieldChange(field as Leaves<OTCFieldsData>, value);
      };
      return (
        <FloatingRateLoanProductLayout
          formData={formData}
          disclosureContext={disclosureContext}
          handleFieldChange={typedHandler}
          errors={errors}
          touched={touched}
          requiredFields={requiredFields}
          enableLoanOpenTermSupport={enableLoanOpenTermSupport}
        />
      );
    }

    if (isRepoProductFields(formData)) {
      const typedHandler: (field: Leaves<OTCRepoFieldsData>, value: string | boolean | null | undefined) => void = (
        field,
        value
      ) => {
        handleFieldChange(field as Leaves<OTCFieldsData>, value);
      };
      return (
        <RepoProductLayout
          formData={formData}
          disclosureContext={disclosureContext as OTCFieldsContext & { rowData: RepoDealProductBuilderRow }}
          handleFieldChange={typedHandler}
          errors={errors}
          touched={touched}
          requiredFields={requiredFields}
        />
      );
    }

    logger.error(new Error(`DealProductBuilder: Invalid product type`), {
      extra: {
        formData,
      },
    });
    return null;
  };

  const productType = (formData.productType ?? 'forward') as OTCProductType;
  const rowDataForLabels = disclosureContext.rowData;
  const loanRowData =
    isFixedRateLoanProductRow(rowDataForLabels) || isFloatingRateLoanProductRow(rowDataForLabels)
      ? rowDataForLabels
      : null;
  const isLoanProduct = loanRowData !== null;
  const optionRowData = isOptionProductRow(rowDataForLabels) ? rowDataForLabels : null;
  const isRepoProduct = isRepoProductRow(rowDataForLabels);
  const repoRowData = isRepoProduct ? rowDataForLabels : null;

  const productDisplay = OTC_PRODUCT_DISPLAY_MAP[productType];

  return (
    <>
      <DialogContent overflow="hidden" style={{ order: 2, height: '100%', overflow: 'auto', padding: 0 }}>
        <form onSubmit={handleSave} id="otc-fields-dialog-form">
          <VStack gap="spacing12" p="spacing16" w="100%">
            <Stack background="backgroundCard" p="spacing16" gap="spacing6" w="100%">
              <CardRow label="Product Type" value={productDisplay.productType} />
              {productDisplay.rateType && <CardRow label="Rate Type" value={productDisplay.rateType} />}
              <CardRow
                label={isRepoProduct || isLoanProduct ? 'Borrowed Asset' : 'Underlying'}
                value={
                  disclosureContext.rowData.baseAsset ? (
                    <CurrencyRenderer
                      currencyString={disclosureContext.rowData.baseAsset}
                      showIcon={true}
                      iconSize={20}
                    />
                  ) : null
                }
              />
              {isRepoProduct && repoRowData && (
                <CardRow
                  label="Repo Asset"
                  value={
                    repoRowData.collateralAsset ? (
                      <CurrencyRenderer currencyString={repoRowData.collateralAsset} showIcon={true} iconSize={20} />
                    ) : null
                  }
                />
              )}
              {optionRowData && (
                <CardRow label="Type" value={optionRowData.optionType === OptionTypeEnum.Call ? 'Call' : 'Put'} />
              )}
              {loanRowData && (
                <>
                  <CardRow
                    label="Collateralization"
                    value={formatLoanSecurityTypeDisplay(loanRowData.loanSecurityType)}
                  />
                  {!isUnsecuredLoanSecurityType(loanRowData.loanSecurityType) && (
                    <CardRow
                      label="Collateral Asset"
                      value={
                        loanRowData.collateralAsset ? (
                          <CurrencyRenderer
                            currencyString={loanRowData.collateralAsset}
                            showIcon={true}
                            iconSize={20}
                          />
                        ) : null
                      }
                    />
                  )}
                </>
              )}
            </Stack>

            {renderProductLayout()}
          </VStack>
        </form>
      </DialogContent>
      <HStack
        style={{ order: 3 }}
        justifyContent="space-between"
        gap="spacing12"
        p="spacing16"
        w="100%"
        borderTop="1px solid"
        borderColor="backgroundDivider"
        onMouseEnter={setAllFieldsTouched}
      >
        <Button variant={ButtonVariants.Default} onClick={handleCancel}>
          Cancel
        </Button>
        <HStack gap="spacing12">
          <Button variant={ButtonVariants.Default} onClick={handleClear}>
            Clear
          </Button>
          <Button type="submit" form="otc-fields-dialog-form" variant={ButtonVariants.Primary} disabled={!isValid}>
            Confirm
          </Button>
        </HStack>
      </HStack>
    </>
  );
};

interface OTCFieldsDialogProps {
  disclosure: ReturnType<typeof useDisclosureWithContext<OTCFieldsContext>>;
  onSave: (otcFields: OTCFieldsData) => void;
  title: string;
}

export const OTCFieldsDialog = ({ disclosure, onSave, title }: OTCFieldsDialogProps) => {
  return (
    <Dialog
      title={title}
      width="600px"
      showClose
      isOpen={disclosure.isOpen}
      close={disclosure.close}
      controlledContent
      suppressAutoEnterForm
    >
      {disclosure.context && (
        <OTCFieldsDialogContent
          title={title}
          disclosure={disclosure}
          disclosureContext={disclosure.context}
          onSave={onSave}
        />
      )}
    </Dialog>
  );
};
